import React from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { AnalyticsData } from "../../../Types/Dashboard";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";

interface ChartsSectionProps {
  analytics: AnalyticsData;
  loading?: boolean;
}

export default function ChartsSection({
  analytics,
  loading,
}: ChartsSectionProps) {
  // Note: period selector is for future use when analytics endpoint supports it

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {[...Array(4)].map((_, i) => (
          <div
            key={i}
            className="bg-white rounded-lg shadow p-6 animate-pulse"
          >
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        ))}
      </div>
    );
  }

  // Format data for charts
  const formatDate = (dateString: string) => {
    try {
      return format(parseISO(dateString), "dd MMM", { locale: fr });
    } catch {
      return dateString;
    }
  };

  const usersEvolutionData = (analytics.users_evolution || []).map((item) => ({
    date: formatDate(item.date),
    count: item.count,
  }));

  const bookingsEvolutionData = (analytics.bookings_evolution || []).map((item) => ({
    date: formatDate(item.date),
    count: item.count,
  }));

  const revenueEvolutionData = (analytics.revenue_evolution || []).map((item) => ({
    date: formatDate(item.date),
    total: item.total,
  }));

  const bookingsByStatusData = (analytics.bookings_by_status || []).map((item) => ({
    status: item.status,
    count: item.count,
  }));

  const ordersByStatusData = (analytics.orders_by_status || []).map((item) => ({
    status: item.status,
    count: item.count,
  }));

  return (
    <div className="space-y-6 mb-6">
      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Users Evolution */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">
            Évolution des Utilisateurs
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={usersEvolutionData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="count"
                stroke="#3b82f6"
                strokeWidth={2}
                name="Utilisateurs"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Bookings Evolution */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">
            Évolution des Sessions
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={bookingsEvolutionData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="count"
                stroke="#10b981"
                strokeWidth={2}
                name="Sessions"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Revenue Evolution */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Évolution des Revenus</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={revenueEvolutionData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip formatter={(value: any) => `$${value} USD`} />
              <Legend />
              <Line
                type="monotone"
                dataKey="total"
                stroke="#f59e0b"
                strokeWidth={2}
                name="Revenus (USD)"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Bookings by Status */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">
            Répartition des Sessions par Statut
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={bookingsByStatusData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="status" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="count" fill="#10b981" name="Nombre" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Orders by Status */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">
            Répartition des Commandes par Statut
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={ordersByStatusData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="status" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="count" fill="#8b5cf6" name="Nombre" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
